import { Host, h } from "@stencil/core";
import { computePosition, autoUpdate, flip, offset, shift } from "@floating-ui/dom";
let id = 0;
// width of the dropdown popover
const width = {
    small: '12.5rem', // 200px
    medium: '17.5rem', // 280px
    large: '22.5rem' // 360px
};
// alignment equivalent for placement
const alignment = {
    center: 'bottom',
    left: 'bottom-start',
    right: 'bottom-end'
};
export class CoreDropdown {
    constructor() {
        this.dropdownId = `dropdown-${++id}`;
        this.toggleDropdown = () => {
            this.open = !this.open;
        };
        this.handleKeyDown = (event) => {
            const triggerElement = document.getElementById(`${this.dropdownId}`).querySelector('[name="trigger-element"]').firstChild.firstChild;
            const dropdownList = document.getElementById(`${this.dropdownId}-list`);
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                // toggle dropdown on trigger element click
                if (event.composedPath().includes(this.element)) {
                    this.toggleDropdown();
                }
                // should trigger click event on the custom action item from the parent
                if (event.target.classList.contains('list-item-action')) {
                    event.target.firstChild.click();
                }
            }
            else if (event.key === 'Escape' || event.key === 'Tab') {
                // reset currentIndex and scrolltop to make navigation easier
                dropdownList.scrollTop = 0;
                this.currentIndex = -1;
                this.open = false;
                // @ts-expect-error
                triggerElement.focus();
            }
            else if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.handleArrowDown(dropdownList.querySelectorAll('li.item:not(.item--disabled)'));
            }
            else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.handleArrowUp(dropdownList.querySelectorAll('li.item:not(.item--disabled)'));
            }
        };
        this.alignment = 'center';
        this.width = 'small';
        this.format = 'label only';
        this.allSelectedItems = [];
        this.open = false;
        this.placement = 'bottom';
        this.currentIndex = undefined;
        this.trigger = undefined;
        this.popover = undefined;
    }
    async selectedItemsChangeHandler(event) {
        // early return if the selected element is outside of the dropdown
        if (!event.composedPath().includes(document.getElementById(`${this.dropdownId}-list`)))
            return;
        const sectionAlreadyexists = this.allSelectedItems.find(element => element.sectionId === event.detail.sectionId);
        if (sectionAlreadyexists) {
            if (event.detail.selectedItems.length === 0) {
                this.allSelectedItems = this.allSelectedItems.filter(section => section.sectionId !== event.detail.sectionId);
            }
            if (event.detail.format === 'selection') {
                this.allSelectedItems = this.allSelectedItems.filter(section => section.sectionId !== event.detail.sectionId);
                this.allSelectedItems.push(event.detail);
            }
        }
        else {
            this.allSelectedItems.push(event.detail);
        }
        this.allSelectedItems = [...this.allSelectedItems];
        this.coreDropdownItemsChange.emit(this.allSelectedItems);
    }
    async handleDropdownOpening(newValue, oldValue) {
        if (newValue === oldValue)
            return;
        this.open = newValue;
        if (this.open) {
            await this.handlePopover();
            // to allow transition on popover opening, it is is displayed with opacity 0 before removing the hidden class
            this.popover.style.display = 'block';
            setTimeout(() => {
                this.popover.classList.remove('items-list--hidden');
            }, 100);
        }
        else {
            // to allow transition on popover closing, the hidden class is added before adding display: none
            this.popover.classList.add('items-list--hidden');
            setTimeout(() => {
                this.popover.style.display = 'none';
                // retrieve dropdown content from the body on close
                this.element.querySelector('.dropdown-wrapper').append(this.popover);
            }, 100);
        }
    }
    disconnectedCallback() {
        this.open = false;
    }
    async updatePosition(reference, floating) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        await computePosition(reference, floating, {
            // choosing placement depending on the alignment property
            placement: alignment[this.alignment],
            middleware: [offset(4), flip(), shift()]
        }).then(({ x, y, placement }) => {
            Object.assign(floating.style, {
                top: `${y}px`,
                left: `${x}px`,
                width: width[this.width]
            });
            const side = placement.split('-')[0];
            this.placement = side;
        });
    }
    async handlePopover() {
        const reference = this.element.querySelector('.trigger-element');
        const floating = document.getElementById(`${this.dropdownId}-list`);
        if (floating) {
            this.popover = floating;
            document.body.append(this.popover);
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            autoUpdate(reference, floating, async () => {
                await this.updatePosition(reference, floating);
            });
        }
    }
    async componentDidLoad() {
        // on click outside close dropdown
        document.addEventListener('click', event => {
            if (!event.composedPath().includes(this.element) && !event.composedPath().includes(document.getElementById(`${this.dropdownId}-list`))) {
                this.open = false;
            }
        });
        if (document.getElementById(`${this.dropdownId}-list`)) {
            document.getElementById(`${this.dropdownId}-list`).addEventListener('keydown', event => {
                this.handleKeyDown(event);
            });
        }
    }
    // Helper function to handle arrow down navigation
    handleArrowDown(items) {
        if (this.currentIndex < items.length - 1) {
            this.currentIndex++;
        }
        else {
            this.currentIndex = 0; // Wrap to the beginning.
        }
        // @ts-expect-error
        items[this.currentIndex].focus();
    }
    // Helper function to handle arrow up navigation
    handleArrowUp(items) {
        if (this.currentIndex > 0) {
            this.currentIndex--;
        }
        else {
            this.currentIndex = items.length - 1; // Wrap to the end.
        }
        // @ts-expect-error
        items[this.currentIndex].focus();
    }
    render() {
        return (h(Host, { key: 'e1bc5b704a9a8d3163d531159311098ff58e330f' }, h("div", { key: 'a5fcc046bb12f5ecdfe1e22978a57e80d4597e71', class: `core-dropdown dropdown-width--${this.width} dropdown-alignment--${this.alignment} dropdown--${this.open ? 'open' : 'closed'}` }, h("div", { key: '485a5a8dfc7bb4b4b27b1e7cc7ebd2291a28f088', class: "dropdown-wrapper" }, h("div", { key: 'd79877e5135cc6d76a92d8d54be9f0435fc9b9c9', class: "trigger-element", "aria-controls": `${this.dropdownId}-list`, "aria-expanded": this.open ? 'true' : 'false', "aria-haspopup": "list", id: this.dropdownId, onClick: this.toggleDropdown, onKeyDown: this.handleKeyDown, tabIndex: 0 }, h("slot", { key: '81bad65812d3af9d6ba01fbd0a24157c0a68c287', name: "trigger-element" }, 
        // Adding default button if there is no trigger-element slot specified
        !this.element.querySelector('[slot="trigger-element"]') && h("core-button", { label: "button" }))), h("div", { key: '18310ff2151d7cdac0a6c0b0de75fee13a2f8ca6', tabIndex: -1, role: "list", class: `core-dropdown-items-list typography-body-medium-regular items-list--hidden items-list--${this.placement}`, id: `${this.dropdownId}-list` }, h("slot", { key: '9efed4d7aa6defb0b932b9676f07ddcacd82179a' }))))));
    }
    static get is() { return "core-dropdown"; }
    static get originalStyleUrls() {
        return {
            "$": ["core-dropdown.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["core-dropdown.css"]
        };
    }
    static get properties() {
        return {
            "alignment": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "'left' | 'center' | 'right'",
                    "resolved": "\"center\" | \"left\" | \"right\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Align the dropdown with the trigger element."
                },
                "attribute": "alignment",
                "reflect": false,
                "defaultValue": "'center'"
            },
            "width": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "'small' | 'medium' | 'large'",
                    "resolved": "\"large\" | \"medium\" | \"small\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Change the width of the dropdown."
                },
                "attribute": "width",
                "reflect": false,
                "defaultValue": "'small'"
            },
            "format": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "'label only' | 'icon' | 'checkbox'",
                    "resolved": "\"checkbox\" | \"icon\" | \"label only\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Change the type of item in the dropdown list."
                },
                "attribute": "format",
                "reflect": false,
                "defaultValue": "'label only'"
            }
        };
    }
    static get states() {
        return {
            "allSelectedItems": {},
            "open": {},
            "placement": {},
            "currentIndex": {},
            "trigger": {},
            "popover": {}
        };
    }
    static get events() {
        return [{
                "method": "coreDropdownItemsChange",
                "name": "coreDropdownItemsChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the selected values of the dropdown have changed."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "open",
                "methodName": "handleDropdownOpening"
            }];
    }
    static get listeners() {
        return [{
                "name": "coreDropdownSectionsItemsChange",
                "method": "selectedItemsChangeHandler",
                "target": "document",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=core-dropdown.js.map
